import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Loader2, Calendar, MapPin, Users, ArrowRight, LogIn } from "lucide-react";
import axios from "axios";

/**
 * Student-facing attendance check-in page.
 * Route: /attendance/{qr_token}
 *
 * Flow:
 *  1. Parse qr_token from URL
 *  2. Call /sessions/{token}/info to show session details (public)
 *  3. If user not logged in → prompt login with return URL
 *  4. If logged in → auto check-in via POST /attendance/check-in/{token}
 *  5. Show success state + continue to dashboard button
 */
export const AttendancePage = ({ apiUrl, authToken, lang = "ms", qrToken, onRequireLogin, onContinue }) => {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [result, setResult] = useState(null); // { already_checked_in, checked_in_at, session }
  const [error, setError] = useState(null);

  // Load session info (public)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${apiUrl}/sessions/${qrToken}/info`)
      .then((res) => { if (!cancelled) setSessionInfo(res.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'QR tidak sah'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiUrl, qrToken]);

  // Auto check-in once session info loaded AND user is logged in
  useEffect(() => {
    if (!sessionInfo || !authToken || result || checkingIn) return;
    if (!sessionInfo.is_open) {
      setError(sessionInfo.window_msg || 'Kehadiran tidak dibuka');
      return;
    }
    setCheckingIn(true);
    axios
      .post(`${apiUrl}/attendance/check-in/${qrToken}`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => setResult(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Gagal check-in'))
      .finally(() => setCheckingIn(false));
  }, [sessionInfo, authToken, qrToken, apiUrl, result, checkingIn]);

  const dateStr = sessionInfo?.date
    ? new Date(sessionInfo.date).toLocaleString(lang === 'en' ? 'en-MY' : 'ms-MY', { dateStyle: 'full', timeStyle: 'short' })
    : '';

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="attendance-page">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-6">
          <h1 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Inter, sans-serif' }}>BSS Portal</h1>
          <p className="text-[10px] text-[rgba(0,0,0,0.4)] tracking-wide">ATTENDANCE CHECK-IN</p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-[24px] shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-hidden border border-[rgba(0,0,0,0.04)]">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 size={32} className="animate-spin text-[#2563EB] mx-auto mb-3" />
              <p className="text-sm text-[rgba(0,0,0,0.5)]">{lang === 'en' ? 'Loading session...' : 'Memuat sesi...'}</p>
            </div>
          ) : error && !sessionInfo ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                <XCircle size={32} className="text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-[#0A0A0A] mb-1">{lang === 'en' ? 'Invalid QR' : 'QR Tidak Sah'}</h2>
              <p className="text-sm text-[rgba(0,0,0,0.5)]">{error}</p>
            </div>
          ) : (
            <>
              {/* Session header */}
              <div className="p-6 bg-[#0A0A0A] text-white">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2563EB] mb-1">
                  {sessionInfo.batch_name}
                </p>
                <h2 className="text-xl font-bold leading-tight">{sessionInfo.topic || (lang === 'en' ? 'Class Session' : 'Sesi Kelas')}</h2>
                <div className="flex items-center gap-3 mt-3 text-xs text-white/60">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {dateStr}</span>
                </div>
                {(sessionInfo.teacher || sessionInfo.location) && (
                  <div className="flex items-center gap-3 mt-1 text-xs text-white/60">
                    {sessionInfo.teacher && <span className="flex items-center gap-1"><Users size={12} /> {sessionInfo.teacher}</span>}
                    {sessionInfo.location && <span className="flex items-center gap-1"><MapPin size={12} /> {sessionInfo.location}</span>}
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-6">
                {!authToken ? (
                  <div className="text-center" data-testid="require-login-state">
                    <div className="w-16 h-16 rounded-full bg-[#F7F7F7] flex items-center justify-center mx-auto mb-3">
                      <LogIn size={28} className="text-[#0A0A0A]" />
                    </div>
                    <p className="text-sm font-semibold text-[#0A0A0A] mb-1">{lang === 'en' ? 'Login required' : 'Log masuk dahulu'}</p>
                    <p className="text-xs text-[rgba(0,0,0,0.45)] mb-4">
                      {lang === 'en' ? 'You must sign in to record your attendance.' : 'Anda perlu log masuk untuk merekodkan kehadiran.'}
                    </p>
                    <button
                      onClick={onRequireLogin}
                      className="w-full py-3 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-[#1a1a1a] transition-colors"
                      data-testid="attendance-login-btn"
                    >
                      {lang === 'en' ? 'Sign in to continue' : 'Log Masuk untuk Teruskan'}
                    </button>
                  </div>
                ) : error ? (
                  <div className="text-center" data-testid="attendance-error-state">
                    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                      <XCircle size={28} className="text-red-500" />
                    </div>
                    <p className="text-sm font-semibold text-[#0A0A0A] mb-1">{lang === 'en' ? 'Cannot check in' : 'Tidak boleh check-in'}</p>
                    <p className="text-xs text-[rgba(0,0,0,0.5)]">{error}</p>
                  </div>
                ) : checkingIn ? (
                  <div className="text-center py-4">
                    <Loader2 size={32} className="animate-spin text-[#2563EB] mx-auto mb-3" />
                    <p className="text-sm text-[rgba(0,0,0,0.5)]">{lang === 'en' ? 'Recording attendance...' : 'Merekod kehadiran...'}</p>
                  </div>
                ) : result ? (
                  <div className="text-center" data-testid="attendance-success-state">
                    <div className="w-20 h-20 rounded-full bg-[#F7F7F7] flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={40} className="text-[#2563EB]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A0A0A] mb-1">
                      {result.already_checked_in
                        ? (lang === 'en' ? 'Already checked in' : 'Sudah hadir')
                        : (lang === 'en' ? 'Attendance recorded!' : 'Kehadiran direkod!')}
                    </h3>
                    <p className="text-xs text-[rgba(0,0,0,0.5)] mb-1">
                      {new Date(result.checked_in_at).toLocaleString(lang === 'en' ? 'en-MY' : 'ms-MY', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                    <p className="text-xs text-[rgba(0,0,0,0.4)] mb-5">
                      {lang === 'en' ? 'Thank you & enjoy the class!' : 'Terima kasih - selamat belajar!'}
                    </p>
                    <button
                      onClick={onContinue}
                      className="w-full py-3 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-[#1a1a1a] transition-colors flex items-center justify-center gap-2"
                      data-testid="attendance-continue-btn"
                    >
                      {lang === 'en' ? 'Continue to dashboard' : 'Ke Dashboard'} <ArrowRight size={14} />
                    </button>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-[10px] text-[rgba(0,0,0,0.3)] mt-4">© 2026 BSS PORTAL</p>
      </div>
    </div>
  );
};
